import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { DatePickerSpinner } from 'components/Form/DatePickerSpinner/DatePickerSpinner';
import { SelectableTag } from 'components/Form/SelectableTag/SelectableTag';
import { Alert } from 'components/UI/Alert/Alert';
import { Spinner } from 'components/UI/Spinner/Spinner';
import { useMarketplace } from 'hooks/useMarketplace';
import { useMediaQuery } from 'hooks/useMediaQuery';
import { useTimeZone } from 'hooks/useTimeZone';
import moment from 'moment-timezone';
import React from 'react';
import { useSelector } from 'react-redux';
import { dispatch } from 'store';
import { Staff } from 'types/Staff';
import { fetchWalkingAvailabilityThunk } from 'actions/onlineBooking/actions/OnlineBookingWalkingActions';
import {
    buildWalkingAvailabilityRequestKey,
    deriveWalkingInitialSelectedDate,
    deriveWalkingMaxDate,
    distinctWalkingServiceIds,
    leadTimeToWeeks,
    walkingAvailabilityCovers,
    walkingPetsForService
} from 'actions/onlineBooking/actions/OnlineBookingWalkingRequests';
import { getOlbDateLimits } from 'reducers/masterData/MasterDataSelectors';
import {
    getOnlineBookingWalkingAddressId,
    ONLINE_BOOKING_SESSION_SELECTORS
} from 'reducers/onlineBooking/OnlineBookingSelectors';
import { OnlineBookingSessionItem, SessionBusinessArea } from 'types/OnlineBooking';
import {
    applyWalkingDateSelection,
    applyWalkingTimeSelection,
    formatWalkingHourLabel,
    isSameWalkingTime,
    WalkingAvailability,
    WalkingAvailableHour,
    walkingHoursForSelectedDay,
    SESSION_AREA_COPY
} from 'types/Walking';
import classes from 'components/OnlineBooking/DateTimePicker/DateTimePicker.module.scss';

interface WalkingDateTimePickerProps {
    businessArea: SessionBusinessArea;
    preferredStaff?: Staff;
    items: Array<OnlineBookingSessionItem>;
    onChange: (updatedItems: Array<OnlineBookingSessionItem>) => void;
}

export const WalkingDateTimePicker: React.FC<WalkingDateTimePickerProps> = ({
    businessArea,
    items,
    preferredStaff,
    onChange
}) => {
    const timeZone = useTimeZone();
    const marketplace = useMarketplace();
    const isMobile = useMediaQuery('mobile-and-tablet');
    const availabilityCache = useSelector(
        ONLINE_BOOKING_SESSION_SELECTORS[businessArea].availability
    );
    const addressId = useSelector(getOnlineBookingWalkingAddressId);
    const olbDateLimits = useSelector(getOlbDateLimits);
    const appliedNextAvailable = React.useRef(Boolean(items[0]?.date));
    // Request keys refetched since this step mounted: cached availability from an earlier visit
    // is never shown as fresh, and each key is refetched only once per mount.
    const refreshedKeys = React.useRef(new Set<string>());

    const today = React.useMemo(() => moment().tz(timeZone), [timeZone]);
    const selectedDate = items[0]?.date;
    const selectedTime = items[0]?.time;
    const displayDate = React.useMemo(() => selectedDate ?? today, [selectedDate, today]);
    const numberOfPets = items.length;
    const staffId = preferredStaff?.id;
    const dateLimits = marketplace.onlineBookingPreferences.bookingDateLimits;
    const maxTimeframeWeeks = leadTimeToWeeks(dateLimits.to);
    const minLeadSeconds =
        olbDateLimits.from.find(limit => limit.id === dateLimits.from)?.offsetSeconds ?? 0;
    const maxLeadSeconds = olbDateLimits.to.find(limit => limit.id === dateLimits.to)
        ?.offsetSeconds;
    const earliestDateTime = React.useMemo(
        () => today.clone().add(minLeadSeconds, 'seconds'),
        [today, minLeadSeconds]
    );
    const minDate = React.useMemo(() => earliestDateTime.clone(), [earliestDateTime]);
    const serviceIds = React.useMemo(() => distinctWalkingServiceIds(items), [items]);
    const startDate = minDate.format('YYYY-MM-DD');
    const serviceIdsKey = serviceIds.join(',');

    const requestKeys = serviceIds.map(serviceId =>
        buildWalkingAvailabilityRequestKey(
            marketplace.id,
            addressId,
            walkingPetsForService(items, serviceId),
            serviceId,
            staffId,
            maxTimeframeWeeks
        )
    );
    const cacheEntries = requestKeys.map(requestKey => availabilityCache[requestKey]);

    const isLoading =
        serviceIds.length > 0 &&
        cacheEntries.some(
            (entry, index) =>
                !refreshedKeys.current.has(requestKeys[index]) ||
                !entry ||
                entry.loading ||
                (!entry.data && !entry.error)
        );
    const fetchError = cacheEntries.find(entry => entry?.error)?.error;
    const responses: Array<WalkingAvailability> = cacheEntries
        .map(entry => entry?.data)
        .filter((data): data is WalkingAvailability => Boolean(data));
    const reasonKey = responses.map(response => response.reason).join('|');
    const selectedDateStr = displayDate.format('YYYY-MM-DD');
    const hours =
        !isLoading && !fetchError && responses.length === serviceIds.length
            ? walkingHoursForSelectedDay(responses, selectedDateStr).filter(hour =>
                  moment.tz(hour.datetime, timeZone).isSameOrAfter(earliestDateTime)
              )
            : [];
    const morningHours = hours.filter(hour => moment.tz(hour.datetime, timeZone).hour() < 12);
    const afternoonHours = hours.filter(hour => moment.tz(hour.datetime, timeZone).hour() >= 12);
    const maxDate = React.useMemo(() => {
        const limit = maxLeadSeconds
            ? today.clone().add(maxLeadSeconds, 'seconds')
            : today.clone().add(maxTimeframeWeeks, 'weeks');

        return moment.tz(
            deriveWalkingMaxDate(responses, limit.format('YYYY-MM-DD')),
            'YYYY-MM-DD',
            timeZone
        );
    }, [responses, today, maxLeadSeconds, maxTimeframeWeeks, timeZone]);

    React.useEffect(() => {
        if (!serviceIdsKey) {
            return;
        }

        serviceIds.forEach((serviceId, index) => {
            const requestKey = requestKeys[index];
            const force = !refreshedKeys.current.has(requestKey);

            if (!force && walkingAvailabilityCovers(cacheEntries[index]?.data, selectedDateStr)) {
                return;
            }

            refreshedKeys.current.add(requestKey);

            dispatch(
                fetchWalkingAvailabilityThunk(
                    businessArea,
                    serviceId,
                    addressId,
                    staffId,
                    startDate,
                    walkingPetsForService(items, serviceId),
                    maxTimeframeWeeks,
                    { force }
                )
            ).catch(() => undefined);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        businessArea,
        serviceIdsKey,
        addressId,
        staffId,
        startDate,
        numberOfPets,
        maxTimeframeWeeks,
        selectedDateStr
    ]);

    React.useEffect(() => {
        reasonKey.split('|').forEach(reason => {
            if (reason) {
                console.debug(
                    `${SESSION_AREA_COPY[businessArea].label} availability reason`,
                    reason
                );
            }
        });
    }, [businessArea, reasonKey]);

    React.useEffect(() => {
        if (appliedNextAvailable.current || isLoading || fetchError || serviceIds.length === 0) {
            return;
        }

        if (responses.length !== serviceIds.length) {
            return;
        }

        appliedNextAvailable.current = true;

        const nextDate = deriveWalkingInitialSelectedDate(
            selectedDate?.format('YYYY-MM-DD'),
            responses.map(response => response.nextAvailableDate),
            minDate.format('YYYY-MM-DD')
        );

        if (selectedDate?.format('YYYY-MM-DD') === nextDate) {
            return;
        }

        onChange(applyWalkingDateSelection(items, moment.tz(nextDate, 'YYYY-MM-DD', timeZone)));
    }, [
        isLoading,
        fetchError,
        responses,
        serviceIds.length,
        selectedDate,
        minDate,
        items,
        onChange,
        timeZone
    ]);

    const dateChangeHandler = React.useCallback(
        (date: moment.Moment) => {
            onChange(applyWalkingDateSelection(items, date));
        },
        [items, onChange]
    );

    const timeChangeHandler = React.useCallback(
        (hour: WalkingAvailableHour) => {
            onChange(applyWalkingTimeSelection(items, displayDate, hour, timeZone, preferredStaff));
        },
        [items, displayDate, timeZone, preferredStaff, onChange]
    );

    return (
        <>
            {fetchError && (
                <Alert
                    variant="error"
                    icon={faInfoCircle}
                    text="Something went wrong. Please go back and try again."
                />
            )}

            <DatePickerSpinner
                mode={isMobile ? 'month' : 'week'}
                minDate={minDate}
                maxDate={maxDate}
                timeZone={timeZone}
                selectedDate={displayDate}
                onChange={dateChangeHandler}
            />

            {isLoading && (
                <div className="w-full text-center mt-6">
                    <Spinner size={50} />
                </div>
            )}

            {!isLoading && !fetchError && hours.length === 0 && (
                <div className={classes.timeGroup}>
                    <p className={classes.noAvailability}>Not available</p>
                </div>
            )}

            {!isLoading && !fetchError && hours.length > 0 && (
                <>
                    <div className={classes.timeGroup}>
                        <p className={classes.timeGroupLabel}>Morning</p>

                        {morningHours.length === 0 && (
                            <p className={classes.noAvailability}>Not available</p>
                        )}

                        <div className={classes.timeGrid}>
                            {morningHours.map(hour => (
                                <SelectableTag
                                    key={hour.datetime}
                                    className={classes.timeTag}
                                    selected={isSameWalkingTime(selectedTime, hour, timeZone)}
                                    onClick={() => timeChangeHandler(hour)}
                                >
                                    {formatWalkingHourLabel(hour.datetime, timeZone)}
                                </SelectableTag>
                            ))}
                        </div>
                    </div>

                    <div className={classes.timeGroup}>
                        <p className={classes.timeGroupLabel}>Afternoon</p>

                        {afternoonHours.length === 0 && (
                            <p className={classes.noAvailability}>Not available</p>
                        )}

                        <div className={classes.timeGrid}>
                            {afternoonHours.map(hour => (
                                <SelectableTag
                                    key={hour.datetime}
                                    className={classes.timeTag}
                                    selected={isSameWalkingTime(selectedTime, hour, timeZone)}
                                    onClick={() => timeChangeHandler(hour)}
                                >
                                    {formatWalkingHourLabel(hour.datetime, timeZone)}
                                </SelectableTag>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </>
    );
};
